// ============================================================
// Native — WebView <-> React Native RPC Protocol
// ============================================================
//
// Two channels:
//   1. RPC      — web.invoke(method) → native handles → response (Promise)
//   2. Notify   — fire-and-forget event on either side (web.notify / on)
//
// Envelope formats are stable across versions; new methods/events are
// added by extending NativeMethods or NativeNotifications.
// ============================================================

import type {
  NativeRecording,
  PitchTrackerConfig,
  PitchTrackerEvent,
  PitchTrackerSessionInfo,
} from './pitch-tracker';

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface DeviceInfoPayload {
  platform: 'ios' | 'android';
  deviceId: string;
  model: string;
  appVersion: string;
  osVersion: string;
  locale: string;
  /**
   * Store country — iOS: App Store storefront, ISO 3166 alpha-3 (`KOR`); Android: SIM, then
   * network country, alpha-2 (`kr`). Absent when unknown and on shells before 1.0.2.
   */
  storeCountry?: string;
  /** Native safe-area insets, also surfaced as `--safe-area-*` CSS vars. */
  safeArea?: SafeAreaInsets;
}

export interface StorageSyncPayload {
  action: 'set' | 'remove' | 'clear';
  key?: string;
  value?: string;
}

export interface ShowAlertParams {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

export interface ShowAlertResult {
  confirmed: boolean;
}

// ------------------------------------------------------------
// UI / Feedback
// ------------------------------------------------------------

export type HapticType =
  | 'light'
  | 'medium'
  | 'heavy'
  | 'success'
  | 'warning'
  | 'error';

export interface ShowToastParams {
  text: string;
  duration?: 'short' | 'long';
  type?: 'success' | 'error' | 'info';
}

export interface ShareParams {
  text?: string;
  url?: string;
  title?: string;
}

export interface ShareResult {
  shared: boolean;
}

// ------------------------------------------------------------
// System / Network
// ------------------------------------------------------------

export interface NetworkStatus {
  online: boolean;
  connectionType: 'wifi' | 'cellular' | 'none' | 'unknown';
}

export interface AppVersion {
  version: string;
  buildNumber: string;
}

// ------------------------------------------------------------
// Deep linking
// ------------------------------------------------------------

export interface DeepLinkPayload {
  /** Full URL received by the native shell. */
  url: string;
  /**
   * `cold-start` when the app was launched by this link.
   * `background` when the app was already running and brought to
   * the foreground by the link.
   */
  source: 'cold-start' | 'background';
}

// ------------------------------------------------------------
// Permissions
// ------------------------------------------------------------

export type PermissionKind = 'camera' | 'photo' | 'notification' | 'microphone';

export type PermissionStatus = 'granted' | 'denied' | 'blocked' | 'unavailable';

export interface PermissionResult {
  status: PermissionStatus;
}

// ------------------------------------------------------------
// Push notifications
// ------------------------------------------------------------

export interface PushTokenResult {
  /** FCM/APNs token. `null` until the OS mints one (e.g. iOS simulator). */
  token: string | null;
  platform: 'ios' | 'android';
}

/**
 * How a push surfaced to the app:
 *   - `foreground`      arrived while the app was active (no tap).
 *   - `background-tap`  the user tapped a notification that woke the app.
 *   - `cold-start-tap`  the user tapped a notification that launched the app.
 */
export type PushReceivedSource =
  | 'foreground'
  | 'background-tap'
  | 'cold-start-tap';

export interface PushReceivedPayload {
  title?: string;
  body?: string;
  /** Server-defined data (FCM data payload), coerced to string values. */
  data?: Record<string, string>;
  source: PushReceivedSource;
}

export interface PushTokenChangedPayload {
  token: string;
  platform: 'ios' | 'android';
}

// ------------------------------------------------------------
// RPC: web → native, awaits response
// ------------------------------------------------------------

/**
 * 이미지 선택 요청. 사진은 아바타로만 쓰이므로 네이티브에서 미리 줄여서 돌려준다 —
 * 원본 해상도가 브릿지를 건너면 WebView 로 수 MB 문자열이 넘어가고, 그게 그대로
 * localStorage 미러를 타 AsyncStorage 까지 간다.
 */
export interface PickImageParams {
  /**
   * 'camera' 는 촬영, 'library' 는 앨범에서 고르기.
   *
   * **비우면 네이티브가 묻는다** — OS 액션시트를 띄우고 고른 쪽으로 연다. 화면에
   * 카메라·앨범 버튼을 나란히 두면 사진 하나 바꾸려고 두 번 눌러야 하고, 어느 쪽을
   * 고를지는 OS 가 이미 잘 묻는 질문이다.
   */
  source?: 'camera' | 'library';
  /** 긴 변 기준 최대 픽셀. 네이티브가 이 안으로 줄여서 준다. */
  maxSize: number;
  /**
   * 묻는 시트의 문구. 문구는 웹이 소유한다(`showAlert` 와 같은 방식) — 네이티브 셸에는
   * i18n 이 없고, 로케일은 웹이 이미 알고 있다. `source` 를 비울 때만 쓴다.
   */
  chooser?: { camera: string; library: string; cancel: string; title: string };
}

export interface PickedImage {
  /** `data:image/jpeg;base64,...` — 그대로 <img src> 에 넣을 수 있다. */
  dataUri: string;
  width: number;
  height: number;
}

export type PickImageResult =
  | { status: 'picked'; image: PickedImage }
  /** 사용자가 그냥 닫았다 — 오류가 아니다. */
  | { status: 'cancelled' }
  /** 권한이 없다. 호출부가 설정으로 보낼지 정한다. */
  | { status: 'denied' }
  | { status: 'failed'; reason: string };

export interface NativeMethods {
  // -- Existing: device & navigation & dialogs
  getDeviceInfo: { params: void; result: DeviceInfoPayload };
  showAlert: { params: ShowAlertParams; result: ShowAlertResult };
  openInAppWeb: { params: { url: string; title?: string }; result: void };
  openExternalUrl: { params: { url: string }; result: void };
  dismissKeyboard: { params: void; result: void };
  setStatusBarStyle: {
    params: { style: 'light-content' | 'dark-content' };
    result: void;
  };

  // -- Tier 1: UI/UX basics
  haptic: { params: { type: HapticType }; result: void };
  showToast: { params: ShowToastParams; result: void };
  share: { params: ShareParams; result: ShareResult };
  openAppSettings: { params: void; result: void };

  // -- Tier 2: system / network
  getNetworkStatus: { params: void; result: NetworkStatus };
  getAppVersion: { params: void; result: AppVersion };

  // -- Tier 3: permissions
  requestPermission: {
    params: { kind: PermissionKind };
    result: PermissionResult;
  };
  getPermissionStatus: {
    params: { kind: PermissionKind };
    result: PermissionResult;
  };

  // -- Tier 3.5: media
  /**
   * 카메라 또는 앨범에서 사진 하나. 권한 요청은 네이티브 쪽 피커가 직접 띄운다 —
   * requestPermission 을 먼저 부를 필요 없다.
   */
  pickImage: { params: PickImageParams; result: PickImageResult };

  // -- Tier 4: push notifications
  getPushToken: { params: void; result: PushTokenResult };

  // -- Tier 5: real-time pitch engine (Practice)
  // The native shell hosts the PitchTracker; these drive a session over RPC
  // and `pitch_frame`/lifecycle events stream back via the `pitchTrackerEvent`
  // notification. Raw PCM never crosses this boundary (GOAL.md invariant).
  startPractice: {
    params: PitchTrackerConfig;
    result: PitchTrackerSessionInfo;
  };
  stopPractice: { params: void; result: void };
  pausePractice: { params: void; result: void };
  resumePractice: { params: void; result: void };
  /**
   * The take the last session recorded (`output.recordToFile`), or null when the
   * session did not opt in, the platform has no implementation, or nothing was
   * captured. Ask **after** `stopPractice` — the file is finalised there.
   *
   * Still no PCM across the boundary: this carries a sandbox path and counters.
   */
  getLastRecording: { params: void; result: NativeRecording | null };
  /**
   * Read one finished take back as base64, or null when the file is gone or is
   * larger than `maxBytes`.
   *
   * This is the one place audio samples cross to JS, and it is deliberate: the
   * WebView cannot read the sandbox path itself, and the upload runs in the web
   * layer. GOAL.md forbids raw PCM crossing **continuously** — the realtime
   * stream — not a finished file handed over once per take.
   *
   * `maxBytes` is the caller's ceiling, checked before the file is read: an
   * oversized take must not be pulled into memory only to be thrown away. A
   * non-positive ceiling means null, not "no limit" — a caller that computed 0
   * by accident must not drag an unbounded file across.
   *
   * The reply travels as base64 inside the bridge's response script, so the byte
   * count here is not the cost: budget for roughly twice it, live at once.
   */
  readRecording: {
    params: { uri: string; maxBytes: number };
    result: { base64: string; byteLength: number } | null;
  };
  /** Delete one take (docs/native/privacy-and-data-handling.md §3). */
  deleteRecording: { params: { uri: string }; result: void };
  /**
   * Delete every take on the device at once — "delete all diagnosis data"
   * (docs/native/privacy-and-data-handling.md §3). `purged: false` means the native side
   * refused because a take is being written; nothing was deleted and the caller has
   * nothing to retry (the next launch sweeps what is left). A build that predates this
   * method answers `Unknown method` (an `ok: false` response → a rejected invoke).
   */
  purgeRecordings: { params: void; result: { purged: boolean } };
  // Runtime config tuning — applied live via `muzly_pitch_update_config`
  // (re-makes the detector). Dev console drives this from sliders (#32).
  updateConfig: { params: Partial<PitchTrackerConfig>; result: void };
}

export type NativeMethod = keyof NativeMethods;
export type NativeParams<M extends NativeMethod> = NativeMethods[M]['params'];
export type NativeResult<M extends NativeMethod> = NativeMethods[M]['result'];

// ------------------------------------------------------------
// Notifications: fire-and-forget, both directions
// ------------------------------------------------------------

export interface NativeNotifications {
  languageChanged: { language: string };
  appStateChanged: { state: 'active' | 'background' | 'inactive' };
  keyboardStateChanged: { visible: boolean; height: number };
  storageSync: StorageSyncPayload;
  reloadApp: void;
  networkStatusChanged: NetworkStatus;
  deepLink: DeepLinkPayload;
  // native → web only: a push arrived or was tapped.
  pushReceived: PushReceivedPayload;
  // native → web only: the push token was issued or rotated.
  pushTokenChanged: PushTokenChangedPayload;
  // native → web only: a PitchTracker event channel — carries `pitch_frame`
  // Analysis Frames plus session lifecycle (started/stopped/error/...).
  pitchTrackerEvent: PitchTrackerEvent;
}

export type NativeEvent = keyof NativeNotifications;
export type NativePayload<E extends NativeEvent> = NativeNotifications[E];

// ------------------------------------------------------------
// Wire envelope (what crosses the WebView boundary)
// ------------------------------------------------------------

export interface NativeRequestEnvelope {
  kind: 'request';
  id: string;
  method: NativeMethod;
  params: unknown;
}

export type NativeResponseEnvelope =
  | {
      kind: 'response';
      id: string;
      ok: true;
      result: unknown;
    }
  | {
      kind: 'response';
      id: string;
      ok: false;
      error: string;
    };

export interface NativeNotificationEnvelope {
  kind: 'notification';
  event: NativeEvent;
  payload: unknown;
}

export type NativeEnvelope =
  | NativeRequestEnvelope
  | NativeResponseEnvelope
  | NativeNotificationEnvelope;

// ------------------------------------------------------------
// Type guards
// ------------------------------------------------------------

export const isNativeEnvelope = (data: unknown): data is NativeEnvelope => {
  if (typeof data !== 'object' || data === null) return false;
  const rec = data as Record<string, unknown>;
  return (
    rec.kind === 'request' ||
    rec.kind === 'response' ||
    rec.kind === 'notification'
  );
};

export const isNativeRequest = (
  env: NativeEnvelope,
): env is NativeRequestEnvelope => env.kind === 'request';

export const isNativeResponse = (
  env: NativeEnvelope,
): env is NativeResponseEnvelope => env.kind === 'response';

export const isNativeNotification = (
  env: NativeEnvelope,
): env is NativeNotificationEnvelope => env.kind === 'notification';
